import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { BasicInfoComponent } from '../../dataset/ngsild/basic-info/basic-info.component';
import { ResourceServerSummaryComponent } from '../../dataset/resource-server-summary/resource-server-summary.component';
import { NgsildItemService } from '../../dataset/ngsild/ngsild-item.service';
import { CatalogueApiService } from '@angular-workspace/services';
import {
  getApiErrorDetail,
  getApiErrorStatus,
} from '../../dataset/ngsild/ngsild-api-error.util';
import type { ModelBasicInfo, ModelMetadataInfo, ModelOnboardingResult } from '../model.models';
import { ModelMetadataInfoComponent } from '../model-metadata-info/model-metadata-info.component';

const STEP_QUERY_KEY = 'step';

interface ModelStep {
  key: string;
  label: string;
}

@Component({
  selector: 'lib-model-home',
  standalone: true,
  imports: [
    CommonModule,
    BasicInfoComponent,
    ModelMetadataInfoComponent,
    ResourceServerSummaryComponent,
  ],
  templateUrl: './model-home.component.html',
  styleUrl: './model-home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModelHomeComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngsildItemService = inject(NgsildItemService);
  private readonly catalogueApi = inject(CatalogueApiService);

  readonly basicInfoRef = viewChild(BasicInfoComponent);
  readonly metadataInfoRef = viewChild(ModelMetadataInfoComponent);

  @Input() publisherRole: 'org-admin' | 'provider' = 'provider';

  readonly back = output<void>();
  readonly complete = output<ModelOnboardingResult>();
  readonly submitError = output<string>();

  readonly isSubmitting = signal(false);

  readonly steps: ModelStep[] = [
    { key: 'basic', label: 'Basic Information' },
    { key: 'metadata', label: 'Metadata Information' },
  ];

  readonly currentStep = signal(0);
  readonly basicValid = signal(false);
  readonly metadataValid = signal(false);

  readonly canGoNext = computed(() => {
    if (this.isSubmitting()) return false;
    return this.currentStep() === 0 ? this.basicValid() : this.metadataValid();
  });

  readonly isLastStep = computed(
    () => this.currentStep() === this.steps.length - 1,
  );

  ngOnInit(): void {
    const raw = this.route.snapshot.queryParamMap.get(STEP_QUERY_KEY);
    this.currentStep.set(raw === '1' ? 1 : 0);

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const step = params.get(STEP_QUERY_KEY) === '1' ? 1 : 0;
        if (this.currentStep() !== step) {
          this.currentStep.set(step);
        }
      });
  }

  onBasicValidChange(valid: boolean): void {
    this.basicValid.set(valid);
  }

  onMetadataValidChange(valid: boolean): void {
    this.metadataValid.set(valid);
  }

  isStepActive(index: number): boolean {
    return this.currentStep() === index;
  }

  isStepCompleted(index: number): boolean {
    return index < this.currentStep();
  }

  isStepClickable(index: number): boolean {
    return index <= this.currentStep();
  }

  onStepClick(index: number): void {
    if (this.isStepClickable(index)) {
      this.currentStep.set(index);
      this.syncStepQueryParam();
    }
  }

  onBackClick(): void {
    if (this.currentStep() > 0) {
      this.currentStep.update((s) => s - 1);
      this.syncStepQueryParam();
      return;
    }
    this.back.emit();
  }

  async onNextClick(): Promise<void> {
    const step = this.currentStep();
    if (step === 0) {
      this.basicInfoRef()?.form.markAllAsTouched();
    } else {
      this.metadataInfoRef()?.form.markAllAsTouched();
    }

    if (!this.canGoNext()) return;
    if (this.isLastStep() && !this.metadataInfoRef()?.validateAdditionalInfo()) {
      return;
    }

    if (this.isLastStep()) {
      const basic = this.buildBasicPayload();
      const metadata = this.buildMetadataPayload();
      if (!basic || !metadata) return;

      this.isSubmitting.set(true);
      try {
        const result = await this.ngsildItemService.createModelItem({ basic, metadata });
        await this.submitAdditionalInfo(result.itemId);
        this.resetWizard();
        this.complete.emit({
          payload: result.payload,
          itemId: result.itemId,
        });
      } catch (error) {
        console.error('Failed to create model item:', error);
        const status = getApiErrorStatus(error);
        const detail = getApiErrorDetail(
          error,
          status === 409
            ? 'Item with this name already exists in local catalogue'
            : 'Failed to create model. Please try again.',
        );
        this.submitError.emit(detail);
      } finally {
        this.isSubmitting.set(false);
      }
      return;
    }

    this.currentStep.update((s) => s + 1);
    this.syncStepQueryParam();
  }

  nextButtonLabel(): string {
    if (this.isSubmitting()) return 'Submitting…';
    return this.isLastStep() ? 'Submit' : 'Next →';
  }

  private async submitAdditionalInfo(assetId: string): Promise<void> {
    const items = this.metadataInfoRef()?.getAdditionalInfoItems() ?? [];
    for (const item of items) {
      try {
        await this.catalogueApi.postProviderFeedback({
          assetId,
          type: item.type,
          data: item.data,
        });
      } catch (error) {
        console.error('Failed to submit additional info item:', error);
      }
    }
  }

  private buildBasicPayload(): ModelBasicInfo | null {
    const ref = this.basicInfoRef();
    if (ref) {
      return ref.form.getRawValue() as ModelBasicInfo;
    }
    return null;
  }

  private buildMetadataPayload(): ModelMetadataInfo | null {
    const ref = this.metadataInfoRef();
    if (ref) {
      return ref.form.getRawValue() as ModelMetadataInfo;
    }
    return null;
  }

  private resetWizard(): void {
    this.basicInfoRef()?.resetForm();
    this.metadataInfoRef()?.resetForm();
    this.basicValid.set(false);
    this.metadataValid.set(false);
    this.currentStep.set(0);
    this.syncStepQueryParam();
  }

  private syncStepQueryParam(): void {
    const step = this.currentStep();
    const next = String(step);
    if (this.route.snapshot.queryParamMap.get(STEP_QUERY_KEY) === next) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [STEP_QUERY_KEY]: step },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
